import { FC, useEffect, useState } from 'react';
import { Box, Button, Group, Image, Modal, Radio, Text, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconCalendar, IconExternalLink, IconX } from '@tabler/icons-react';
import { MODAL_RADIUS, ONBOARDING_PARTNERS } from '@/PageComponents/Onboarding/onboarding-config';
import { packageSlug } from '@/PageComponents/Onboarding/onboarding-analytics';
import { PrimaryButton } from '@/PageComponents/Onboarding/components/OnboardingButtons';
import { ConnectSteps, PrivacyNote, SyncTable } from '@/PageComponents/Onboarding/components/IntegrationSync';
import styles from '@/PageComponents/Onboarding/components/IntegrationInfoModal.module.css';

export interface IntegrationInfoModalProps {
    opened: boolean;
    onClose: () => void;
    /** Pre-selects this partner (Enums.IntegrationPartner), e.g. the one already connected. */
    initialPartner?: number | null;
    /** "Connect now" for the package picked (its name): opens the integration settings, where the connection is made. */
    onViewIntegrations: (packageName: string) => void;
    onBookDemo: () => void;
    /** A demo is already booked: no Book a demo. */
    demoBooked?: boolean;
}

/** "Connect your data": what connecting an accounting package does, per package. */
const IntegrationInfoModal: FC<IntegrationInfoModalProps> = ({ opened, onClose, initialPartner, onViewIntegrations, onBookDemo, demoBooked = false }) => {
    const isMobile = useMediaQuery('(max-width: 800px)');
    const defaultPartner = ONBOARDING_PARTNERS.find(p => p.partner === initialPartner) ?? ONBOARDING_PARTNERS[0];
    const [selected, setSelected] = useState(defaultPartner.partner);
    // Start from the default each time it opens.
    useEffect(() => { if (opened) setSelected(defaultPartner.partner); }, [opened, defaultPartner.partner]);
    const partner = ONBOARDING_PARTNERS.find(p => p.partner === selected) ?? defaultPartner;

    return (
        <Modal.Root
            opened={opened}
            onClose={onClose}
            size={1019}
            fullScreen={isMobile}
            // Mantine's default 5dvh offset would cap it shorter on a laptop screen.
            yOffset={16}
            centered
            transitionProps={{ transition: 'pop', exitDuration: 50, duration: 100, timingFunction: 'ease' }}
            classNames={{ content: styles.content, body: styles.body }}
        >
            <Modal.Overlay color="var(--mantine-color-scBlue-5)" backgroundOpacity={0.55} blur={10} />
            <Modal.Content radius={MODAL_RADIUS} aria-label="Connect your data">
            <Modal.Body p={0}>
            <Box className={styles.main}>
                <Group justify="space-between" wrap="nowrap" align="flex-start" gap="md">
                    <Text className={styles.title}>Connect your data</Text>
                    <UnstyledButton id="onboarding-connect-your-data-close" aria-label="Close" onClick={onClose} className={styles.close}>
                        <IconX size={22} stroke={2.5} />
                    </UnstyledButton>
                </Group>

                <Text className={styles.subheading} id="integration-choose-label">Which accounting package do you use?</Text>
                <Radio.Group
                    value={String(selected)}
                    onChange={v => setSelected(Number(v))}
                    aria-labelledby="integration-choose-label"
                >
                    <div className={styles.options}>
                        {ONBOARDING_PARTNERS.map(p => (
                            <Radio.Card key={p.partner} id={`onboarding-package-${packageSlug(p.name).replace(/_/g, '-')}`} value={String(p.partner)} radius={4} className={styles.option} data-testid={`integration-option-${p.partner}`}>
                                <Group gap={12} wrap="nowrap">
                                    <Radio.Indicator size="md" />
                                    <Text className={styles.optionName}>{p.name}</Text>
                                    <Image src={p.logoUrl} alt="" w={44} h={44} fit="contain" className={styles.optionLogo} />
                                </Group>
                            </Radio.Card>
                        ))}
                    </div>
                </Radio.Group>
                <Text className={styles.intro}>
                    If you connect to {partner.name} through our Integrations settings page, your data will sync across ServCraft.
                </Text>

                <Box mt={16}><ConnectSteps partnerName={partner.name} /></Box>
                <Box mt={12}><PrivacyNote /></Box>
                <Box mt={12}><SyncTable partnerName={partner.name} /></Box>

                <Group gap={12} mt={24} className={styles.actions}>
                    <PrimaryButton id="onboarding-connect-now" leftSection={<IconExternalLink size={20} />} onClick={() => onViewIntegrations(partner.name)}>Connect now</PrimaryButton>
                    {!demoBooked && (
                        <>
                            <Text className={styles.or}>or</Text>
                            <Button id="onboarding-book-demo-connect-your-data" color="green" radius="xl" className={styles.bookButton} leftSection={<IconCalendar size={20} />} onClick={onBookDemo}>
                                Book a demo
                            </Button>
                            <Text className={styles.or}>and we will help you get set up</Text>
                        </>
                    )}
                </Group>
            </Box>
            </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};

export default IntegrationInfoModal;
